import { Plus, X } from "@phosphor-icons/react";

import type { Window } from "@/lib/api";
import { cn } from "@/lib/utils";

import { InlineEdit } from "./inline-edit";
import { MissingChip } from "./missing-chip";

const COMPASS_POINTS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];

/** Nearest 45°-compass label for a cardinal orientation in degrees. */
function compassLabel(deg: number | null | undefined): string {
  if (deg == null) {
    return "—";
  }
  const normalized = ((deg % 360) + 360) % 360;
  return COMPASS_POINTS[Math.round(normalized / 45) % 8] ?? "—";
}

const BOOL_OPTIONS = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const BLIND_ELECTRIC_OPTIONS = [
  { value: "true", label: "Electric" },
  { value: "false", label: "Not electric" },
  { value: "null", label: "Unconfirmed" },
];

interface WindowRowProps {
  window: Window;
  onChange: (patch: Partial<Window>) => Promise<void>;
  onRemove: () => Promise<void>;
}

function WindowRow({ window: win, onChange, onRemove }: WindowRowProps) {
  const isRoof = win.window_type === "roof";

  return (
    <div className="border-border-subtle flex flex-wrap items-center gap-2.5 border-t px-5 py-2 text-[13.5px] first:border-t-0">
      <InlineEdit
        value={win.window_type}
        type="select"
        options={[
          { value: "roof", label: "Roof" },
          { value: "facade", label: "Facade" },
        ]}
        onSave={(v) => onChange({ window_type: v })}
        ariaLabel="Window type"
        className={cn(
          "rounded-md px-2 py-0.5 text-[11px] font-semibold",
          isRoof ? "bg-accent/10 text-accent-pressed" : "bg-surface-subtle text-primary",
        )}
      >
        {isRoof ? "Roof" : "Facade"}
      </InlineEdit>

      <span className="text-muted font-mono text-xs">
        <InlineEdit
          value={win.area_m2 != null ? String(win.area_m2) : ""}
          type="number"
          onSave={(v) => onChange({ area_m2: v === "" ? null : Number(v) })}
          ariaLabel="Window area in square metres"
        >
          {win.area_m2 ?? "—"} m²
        </InlineEdit>
        {" · "}
        {compassLabel(win.cardinal_orientation_deg)}{" "}
        <InlineEdit
          value={win.cardinal_orientation_deg != null ? String(win.cardinal_orientation_deg) : ""}
          type="number"
          onSave={(v) => onChange({ cardinal_orientation_deg: v === "" ? null : Number(v) })}
          ariaLabel="Window orientation in degrees"
        >
          {win.cardinal_orientation_deg ?? "—"}°
        </InlineEdit>
      </span>

      {win.cardinal_orientation_deg == null && <MissingChip variant="handoff" />}

      <span className="grow" />

      <InlineEdit
        value={win.window_is_electric ? "true" : "false"}
        type="select"
        options={BOOL_OPTIONS}
        onSave={(v) => onChange({ window_is_electric: v === "true" })}
        ariaLabel="Window is electric"
        className={cn(
          "rounded px-1.5 py-0.5 text-[10.5px] font-semibold",
          win.window_is_electric
            ? "bg-accent/10 text-accent-pressed"
            : "border-border text-muted border",
        )}
      >
        {win.window_is_electric ? "VELUX · electric" : "manual"}
      </InlineEdit>

      <InlineEdit
        value={win.is_openable ? "true" : "false"}
        type="select"
        options={BOOL_OPTIONS}
        onSave={(v) => onChange({ is_openable: v === "true" })}
        ariaLabel="Window is openable"
        className="border-border text-muted rounded border px-1.5 py-0.5 text-[10.5px] font-semibold"
      >
        {win.is_openable ? "openable" : "fixed"}
      </InlineEdit>

      <InlineEdit
        value={win.has_blind ? "true" : "false"}
        type="select"
        options={BOOL_OPTIONS}
        onSave={(v) => onChange({ has_blind: v === "true" })}
        ariaLabel="Has blind"
        className="border-border text-muted rounded border px-1.5 py-0.5 text-[10.5px] font-semibold"
      >
        {win.has_blind ? "blind" : "no blind"}
      </InlineEdit>

      {win.has_blind && (
        <InlineEdit
          value={win.blind_is_electric == null ? "null" : win.blind_is_electric ? "true" : "false"}
          type="select"
          options={BLIND_ELECTRIC_OPTIONS}
          onSave={(v) => onChange({ blind_is_electric: v === "null" ? null : v === "true" })}
          ariaLabel="Blind is electric"
          className="border-border text-muted rounded border px-1.5 py-0.5 text-[10.5px] font-semibold"
        >
          {win.blind_is_electric == null
            ? "blind: unconfirmed"
            : win.blind_is_electric
              ? "blind: electric"
              : "blind: manual"}
        </InlineEdit>
      )}

      <button
        type="button"
        onClick={() => void onRemove()}
        aria-label="Remove window"
        className="text-muted hover:text-destructive shrink-0 rounded p-1 transition-colors"
      >
        <X className="size-3.5" weight="light" />
      </button>
    </div>
  );
}

interface RoomWindowsProps {
  windows: Window[];
  onChange: (windowIndex: number, patch: Partial<Window>) => Promise<void>;
  onAdd: () => Promise<void>;
  onRemove: (windowIndex: number) => Promise<void>;
}

export function RoomWindows({ windows, onChange, onAdd, onRemove }: RoomWindowsProps) {
  return (
    <div>
      <div className="flex items-center justify-between px-5 pt-3 pb-1">
        <span className="text-muted type-label">Windows · {windows.length}</span>
        <button
          type="button"
          onClick={() => void onAdd()}
          className="text-primary focus-visible:outline-ring inline-flex items-center gap-1 text-[12px] font-semibold hover:underline focus-visible:underline focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          <Plus className="size-3.5" weight="light" />
          Add window
        </button>
      </div>
      {windows.length === 0 && (
        <p className="text-muted px-5 pb-2 text-[13px]">No windows yet — add the first one.</p>
      )}
      {windows.map((win, i) => (
        <WindowRow
          key={i}
          window={win}
          onChange={(patch) => onChange(i, patch)}
          onRemove={() => onRemove(i)}
        />
      ))}
    </div>
  );
}
